import classNames from 'classnames';
import React, { ReactNode, useMemo } from 'react';

import { Link } from '@sbu/components.link';

import { EnabledContextActionFragment as ContextActionType } from 'core/graphql';
import { ListItem } from 'shared/components/layout/List';

import { getRecordSchemaVersion } from '../Records';
import { ContextActionProps } from './ContextAction';
import css from './ContextAction.module.scss';
import { replaceSumoBaseUrl, replaceSumoRegion } from './sumoTemplateParams';
import {
  ContextData,
  isValidContextActionUrlTemplate,
  parseTimestampDeltas,
  replaceContextProps,
  TEMPLATE_TS_UNIT,
} from './template';

interface ContextActionMenuItemProps {
  children?: React.ReactNode;
  contextAction: ContextActionType;
  contextData?: ContextActionProps['contextData'];
  onClick?: React.MouseEventHandler;
  sumoLogicRegion?: ContextActionProps['sumoLogicRegion'];
  value?: ContextActionProps['value'];
}

const useUrl = (
  children: ReactNode,
  contextAction: ContextActionType,
  contextData: ContextData | undefined,
  sumoLogicRegion: string | undefined,
  value: string | undefined,
  replaceRecordProps: boolean
) =>
  useMemo(() => {
    const rawValue = value ?? children;
    const timestamp = contextData?.timestamp;
    let success = true;

    let url = contextAction.template.replace(
      /{{value}}/g,
      encodeURIComponent(rawValue as string)
    );

    const isTimestamp = new RegExp(`(\S*)${TEMPLATE_TS_UNIT}+(\S*)`).test(url);

    // If a timestamp doesn't exist in the given context data, replace the
    // template values with an empty string if the template includes a
    // timestamp.
    if (isTimestamp) {
      if (timestamp) {
        const schema = getRecordSchemaVersion(contextData);

        url = parseTimestampDeltas(
          url,
          timestamp,
          schema === 'v3' ? 'ms' : 's'
        );
      } else {
        success = false;
        url = url.replace(new RegExp(TEMPLATE_TS_UNIT, 'g'), '');
      }
    }

    // Replace Sumo Logic region in URL.
    url = replaceSumoRegion(url, sumoLogicRegion);

    // Replace Sumo Logic base URL in URL.
    url = replaceSumoBaseUrl(url);

    // Replace Record properties in the URL.
    if (replaceRecordProps) {
      const { url: resultUrl, success: resultSuccess } = replaceContextProps(
        url,
        contextData ?? {}
      );
      url = resultUrl;
      success = success && resultSuccess;
    }

    return { url, success };
  }, [
    children,
    contextAction.template,
    contextData,
    replaceRecordProps,
    sumoLogicRegion,
    value,
  ]);

const SUMO_BASE_URL_REGEX = /^{{sumobaseurl}}/i;

export const ContextActionMenuItem = ({
  children,
  contextAction,
  contextData,
  onClick,
  sumoLogicRegion,
  value,
}: ContextActionMenuItemProps) => {
  const { success, url } = useUrl(
    children,
    contextAction,
    contextData,
    sumoLogicRegion,
    value,
    true
  );

  if (
    !isValidContextActionUrlTemplate(contextAction.template) &&
    !SUMO_BASE_URL_REGEX.test(contextAction.template)
  ) {
    return null;
  }

  const title = !success
    ? 'Oops, this action was created expecting either a timestamp in context or a configured Sumo Logic region, but none were found.'
    : undefined;

  return (
    <ListItem className={classNames(css.listItem, { [css.error]: !success })}>
      <Link
        data-sumo-test='context-action-action'
        data-metric-id='context-action-menu-item'
        data-ref='context-action-menu-item'
        href={url}
        rel='noopener noreferrer'
        target='_blank'
        title={title}
        variant='a'
        onClick={onClick}
      >
        {contextAction.name}
      </Link>
    </ListItem>
  );
};
